import Cookies from "js-cookie";

const ANALYTICS_ENDPOINT = "/analytics/events";
const ANONYMOUS_ID_KEY = "aapmor_analytics_anonymous_id";
const SESSION_ID_KEY = "aapmor_analytics_session_id";
const SESSION_START_KEY = "aapmor_analytics_session_started_at";

const normalizeText = (value = "") => String(value || "").trim();

const getBaseUrl = () => process.env.REACT_APP_API_URL || "";

const createId = (prefix) =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

export const getAnonymousId = () => {
  const existingId = localStorage.getItem(ANONYMOUS_ID_KEY);

  if (existingId) {
    return existingId;
  }

  const anonymousId = createId("anon");
  localStorage.setItem(ANONYMOUS_ID_KEY, anonymousId);
  return anonymousId;
};

export const getSessionId = () => {
  const existingSessionId = sessionStorage.getItem(SESSION_ID_KEY);

  if (existingSessionId) {
    return existingSessionId;
  }

  const sessionId = createId("session");
  sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  sessionStorage.setItem(SESSION_START_KEY, new Date().toISOString());
  return sessionId;
};

export const getSessionDurationSec = () => {
  const startedAt = sessionStorage.getItem(SESSION_START_KEY);

  if (!startedAt) {
    return 0;
  }

  const diffMs = Date.now() - new Date(startedAt).getTime();
  return Math.max(0, Math.round(diffMs / 1000));
};

const buildContext = () => ({
  anonymousId: getAnonymousId(),
  sessionId: getSessionId(),
  pathname: window.location.pathname,
  pageTitle: document.title,
  referrer: document.referrer || "",
  source: "web",
});

const buildHeaders = () => {
  const authToken = Cookies.get("jwtToken");
  const headers = {
    "Content-Type": "application/json",
  };

  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  return headers;
};

export const inferAiTaskType = (prompt = "", source = "") => {
  const haystack = normalizeText(`${source} ${prompt}`).toLowerCase();

  if (!haystack) return "unknown";
  if (haystack.includes("summary") || haystack.includes("tl;dr")) return "summarization";
  if (haystack.includes("explain")) return "explanation";
  if (haystack.includes("idea")) return "ideation";
  if (haystack.includes("comment")) return "comment_assistance";
  if (haystack.includes("recommend")) return "recommendation";
  return "qna";
};

export const trackEvent = async (
  eventName,
  properties = {},
  options = {},
) => {
  const payload = {
    eventName,
    timestamp: new Date().toISOString(),
    ...buildContext(),
    properties,
  };

  if (options.useBeacon && typeof navigator.sendBeacon === "function") {
    const authToken = Cookies.get("jwtToken");
    const body = JSON.stringify({
      ...payload,
      authToken,
    });
    const blob = new Blob([body], { type: "application/json" });
    navigator.sendBeacon(`${getBaseUrl()}${ANALYTICS_ENDPOINT}`, blob);
    return;
  }

  try {
    await fetch(`${getBaseUrl()}${ANALYTICS_ENDPOINT}`, {
      method: "POST",
      headers: buildHeaders(),
      body: JSON.stringify(payload),
      keepalive: Boolean(options.keepalive),
      credentials: "include",
    });
  } catch (error) {
    if (options.silent !== true) {
      console.error("Analytics tracking error:", error);
    }
  }
};

export const startSessionTracking = () =>
  trackEvent("session_start", {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
  });

export const endSessionTracking = () =>
  trackEvent(
    "session_end",
    {
      durationSec: getSessionDurationSec(),
      pathname: window.location.pathname,
    },
    { useBeacon: true, silent: true },
  );

export const resetSessionTracking = () => {
  sessionStorage.removeItem(SESSION_ID_KEY);
  sessionStorage.removeItem(SESSION_START_KEY);
};
